import Alpine from 'alpinejs';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import Lenis from '@studio-freight/lenis';
import Swiper from 'swiper';
import initListingAjax from './listingAjax.js';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Global
window.Alpine = Alpine;
window.gsap = gsap;

// Inits
Alpine.start();

// Initialize Lenis smooth scroll
let lenis;

function initSmoothScroll() {
	// Create Lenis instance with smooth configuration
	lenis = new Lenis({
		duration: 1.2,
		easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Custom easing for smooth feel
		orientation: 'vertical',
		gestureOrientation: 'vertical',
		smoothWheel: true,
		wheelMultiplier: 1,
		smoothTouch: false,
		touchMultiplier: 2,
		infinite: false,
	});

	// Integrate Lenis with GSAP ScrollTrigger
	function raf(time) {
		lenis.raf(time);
		requestAnimationFrame(raf);
	}

	requestAnimationFrame(raf);

	// Update ScrollTrigger when Lenis scrolls
	lenis.on('scroll', ScrollTrigger.update);

	// Make ScrollTrigger work with Lenis
	ScrollTrigger.scrollerProxy(document.body, {
		scrollTop(value) {
			if (arguments.length) {
				lenis.scrollTo(value, { immediate: true });
			}
			return lenis.scroll;
		},
		getBoundingClientRect() {
			return {
				top: 0,
				left: 0,
				width: window.innerWidth,
				height: window.innerHeight,
			};
		},
		pinType: document.body.style.transform ? 'transform' : 'fixed',
	});

	// Refresh ScrollTrigger after Lenis is ready
	ScrollTrigger.addEventListener('refresh', () => {
		lenis.resize();
	});

	// Initial refresh after a small delay to ensure everything is ready
	setTimeout(() => {
		ScrollTrigger.refresh();
	}, 100);

	// Make Lenis available globally
	window.lenis = lenis;
}

// Fade up animation functionality
function initFadeUpAnimations() {
	// Find all elements with fade-up class
	const fadeUpElements = document.querySelectorAll('.fade-up')
	
	fadeUpElements.forEach((element) => {
		// Get custom delay from data attribute or parse from Tailwind delay class (e.g., delay-300 = 300ms)
		let customDelay = element.getAttribute('data-delay')
		
		// If no data-delay, try to parse from Tailwind delay class (delay-XXX)
		if (!customDelay) {
			const delayClass = Array.from(element.classList).find(cls => cls.startsWith('delay-'))
			if (delayClass) {
				// Extract number from delay-300 format (300 = 300ms)
				const delayMatch = delayClass.match(/delay-(\d+)/)
				if (delayMatch) {
					customDelay = delayMatch[1]
				}
			}
		}

		console.log('in the fade up animation')
		
		const hasMobileNoDelay = element.classList.contains('mobile-no-delay')
		
		// Check if mobile and has mobile-no-delay class
		const isMobile = window.innerWidth < 768
		const delay = (isMobile && hasMobileNoDelay) ? 0 : (customDelay ? parseFloat(customDelay) : 0)
		
		// Set CSS transition delay
		if (delay > 0) {
			element.style.transitionDelay = `${delay}ms`
		}
		
		// Check if element is already in viewport when observed
		const rect = element.getBoundingClientRect()
		const isInViewport = rect.top < window.innerHeight && rect.bottom > 0
		
		// Create intersection observer for scroll trigger
		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					// Add animate class to trigger CSS animation
					entry.target.classList.add('animate')
					observer.unobserve(entry.target) // Only animate once
				}
			})
		}, {
			root: null,
			rootMargin: '0px 0px 0px 0px', // No margin - trigger as soon as any part enters viewport
			threshold: 0 // Trigger when any part of element enters viewport (better for small elements)
		})
		
		// If element is already in viewport, trigger animation immediately
		if (isInViewport) {
			setTimeout(() => {
				element.classList.add('animate')
			}, delay)
		} else {
			// Otherwise observe for when it enters viewport
			observer.observe(element)
		}
	})
}

// Animated counter functionality
function initCounterAnimations() {
	// Find all elements with data-stat attribute
	const statElements = document.querySelectorAll('[data-stat]')
	
	// First pass: Create all counter HTML
	statElements.forEach((element) => {
		const statValue = element.getAttribute('data-stat')
		const animatedHTML = createAnimatedCounterHTML(statValue)
		
		// Replace the content with animated counter HTML
		element.innerHTML = animatedHTML
		element.classList.add('stat-counter')
	})
	
	// Second pass: Calculate max width per font-size group after all elements are rendered
	setTimeout(() => {
		// Group stats by their computed font size
		const fontSizeGroups = new Map()
		
		// First, measure all invisible digits and group by font size
		statElements.forEach(element => {
			const invisibleDigits = element.querySelectorAll('.invisible-digit')
			
			invisibleDigits.forEach(digit => {
				// Temporarily reset to natural width to measure
				digit.style.width = 'auto'
				digit.style.display = 'inline'
				
				// Force reflow to get accurate measurement
				digit.offsetHeight
				
				// Get computed font size
				const computedStyle = window.getComputedStyle(digit)
				const fontSize = computedStyle.fontSize
				
				// Measure the actual width of the invisible digit content
				const naturalWidth = digit.offsetWidth
				
				// Set the original width as a CSS variable
				digit.style.setProperty('--initial-width', `${naturalWidth}px`)
				
				// Group by font size
				if (!fontSizeGroups.has(fontSize)) {
					fontSizeGroups.set(fontSize, { maxWidth: 0, digits: [] })
				}
				
				const group = fontSizeGroups.get(fontSize)
				group.maxWidth = Math.max(group.maxWidth, naturalWidth)
				group.digits.push(digit)
			})
		})
		
		// Third pass: Apply max width per font-size group
		fontSizeGroups.forEach((group, fontSize) => {
			console.log(`🔍 Font size ${fontSize}: max width ${group.maxWidth}px for ${group.digits.length} digits`)
			
			// Set all digits in this font-size group to their group's max width
			group.digits.forEach(digit => {
				digit.style.width = `${group.maxWidth}px`
				digit.style.display = 'inline-block'
			})
		})
		
		// Fourth pass: Set up GSAP ScrollTrigger for each counter
		statElements.forEach(element => {
			const digitColumns = element.querySelectorAll('.digit-column')
			console.log('🔍 Found digit columns:', digitColumns.length)
			console.log('🔍 Element:', element)
			
			// Animate each digit column with staggered timing when in view
			gsap.to(digitColumns, {
				className: 'digit-column animate',
				duration: 0.01, // Instant class addition
				stagger: 0.1, // 100ms delay between each digit
				ease: 'none',
				scrollTrigger: {
					trigger: element,
					start: 'top 80%',
					end: 'bottom 20%',
					once: true,
					onEnter: () => {
						console.log('🔍 ScrollTrigger onEnter fired!')
						// Add animate class to invisible digits
						const currentInvisibleDigits = element.querySelectorAll('.invisible-digit')
						console.log('🔍 Found invisible digits:', currentInvisibleDigits.length)
						
						currentInvisibleDigits.forEach(digit => {
							digit.classList.add('animate')
							console.log('🔍 Added animate class to:', digit.textContent, 'Class list:', digit.classList.toString())
						})
						console.log('🔍 animate class added to invisible digits')
					}
				}
			})
		})
	}, 100) // Small delay to ensure elements are rendered
}

// Create animated counter HTML from stat value
function createAnimatedCounterHTML(statValue) {
	// Enhanced regex to match various number formats: £20m, 5,500, 5.500, 1.2km, etc.
	const match = statValue.match(/^([£$€]?)([\d,\.]+)([a-zA-Z]*)$/)
	if (!match) return statValue
	
	const prefix = match[1] || '' // Currency symbols
	const numberPart = match[2] || '' // The numeric part
	const suffix = match[3] || '' // Units like 'm', 'km', etc.
	
	// Clean the number part by removing commas and handling decimals
	const cleanNumber = numberPart.replace(/,/g, '')
	const hasDecimal = cleanNumber.includes('.')
	
	if (hasDecimal) {
		// Handle decimal numbers like 1.2km
		const [integerPart, decimalPart] = cleanNumber.split('.')
		const integerDigits = integerPart.split('')
		const decimalDigits = decimalPart.split('')
		
		// Create HTML for integer digits
		const integerHTML = integerDigits.map(digit => {
			const digitValue = parseInt(digit)
			const invisibleDigit = `<span class="invisible-digit">${digit}</span>`
			const digitSpans = []
			
			const maxCycles = 2
			const totalDigits = maxCycles * 10 + digitValue + 1
			
			for (let i = 0; i < totalDigits; i++) {
				const currentDigit = i % 10
				const isFinalDigit = i === totalDigits - 1
				const digitClass = isFinalDigit ? 'digit bottom-digit' : 'digit'
				digitSpans.push(`<span class="${digitClass}">${currentDigit}</span>`)
			}
			
			const digitColumn = `<span class="digit-column">${digitSpans.join('')}</span>`
			return `<div class="counter-digit">${invisibleDigit}${digitColumn}</div>`
		}).join('')
		
		// Create HTML for decimal point
		const decimalPoint = `<span class="counter-decimal">.</span>`
		
		// Create HTML for decimal digits
		const decimalHTML = decimalDigits.map(digit => {
			const digitValue = parseInt(digit)
			const invisibleDigit = `<span class="invisible-digit">${digit}</span>`
			const digitSpans = []
			
			const maxCycles = 2
			const totalDigits = maxCycles * 10 + digitValue + 1
			
			for (let i = 0; i < totalDigits; i++) {
				const currentDigit = i % 10
				const isFinalDigit = i === totalDigits - 1
				const digitClass = isFinalDigit ? 'digit bottom-digit' : 'digit'
				digitSpans.push(`<span class="${digitClass}">${currentDigit}</span>`)
			}
			
			const digitColumn = `<span class="digit-column">${digitSpans.join('')}</span>`
			return `<div class="counter-digit">${invisibleDigit}${digitColumn}</div>`
		}).join('')
		
		// Create prefix and suffix HTML with smaller font size
		const prefixHTML = prefix ? `<span class="counter-prefix text-sm lg:text-base">${prefix}</span>` : ''
		const suffixHTML = suffix ? `<span class="counter-suffix text-sm lg:text-base">${suffix}</span>` : ''
		
		return prefixHTML + integerHTML + decimalPoint + decimalHTML + suffixHTML
	} else {
		// Handle whole numbers
		const digits = cleanNumber.split('')
		
		// Create HTML for each digit
		const digitHTML = digits.map(digit => {
			const digitValue = parseInt(digit)
			
			// Create invisible digit for spacing
			const invisibleDigit = `<span class="invisible-digit">${digit}</span>`
			
			// Create count-up sequence with max 2 full rotations
			const digitSpans = []
			
			// Calculate how many cycles we need (max 2 full rotations)
			const maxCycles = 2
			const totalDigits = maxCycles * 10 + digitValue + 1 // +1 for the final digit
			
			// Build the sequence: 0-9, 0-9, then 0 to target digit
			for (let i = 0; i < totalDigits; i++) {
				const currentDigit = i % 10
				const isFinalDigit = i === totalDigits - 1
				const digitClass = isFinalDigit ? 'digit bottom-digit' : 'digit'
				digitSpans.push(`<span class="${digitClass}">${currentDigit}</span>`)
			}
			
			const digitColumn = `<span class="digit-column">${digitSpans.join('')}</span>`
			
			return `<div class="counter-digit">${invisibleDigit}${digitColumn}</div>`
		}).join('')
		
		// Create prefix and suffix HTML with smaller font size
		const prefixHTML = prefix ? `<span class="counter-prefix">${prefix}</span>` : ''
		const suffixHTML = suffix ? `<span class="counter-suffix">${suffix}</span>` : ''
		
		return prefixHTML + digitHTML + suffixHTML
	}
}


// Text animation functionality
function initTextAnimations() {
	// Find all elements with animate-title class
	const animatedElements = document.querySelectorAll('.animate-title, .subtext:not(.noFade) p, .subtext:not(.noFade) li, .subtext:not(.noFade) h1, .subtext:not(.noFade) h2, .subtext:not(.noFade) h3, .subtext:not(.noFade) h4, .subtext:not(.noFade) h5, .subtext:not(.noFade) h6')
	
	animatedElements.forEach((element) => {
		// Check if element already has .extra-split-word spans
		const existingSpans = element.querySelectorAll('.extra-split-word span')
		let innerSpans
		
		if (existingSpans.length > 0) {
			// Element already processed, just apply animation
			innerSpans = existingSpans
		} else {
			// Get the original HTML content
			const originalHTML = element.innerHTML
			
			// Clear the element
			element.innerHTML = ''
			
			// Function to process text and split into words
			const processText = (text, parentElement) => {
				const words = text.split(/(\s+)/)
				
				words.forEach((word) => {
					if (word.trim()) {
						// Create word wrapper
						const wordWrapper = document.createElement('span')
						wordWrapper.className = 'extra-split-word'
						
						// Create inner span for animation
						const innerSpan = document.createElement('span')
						innerSpan.textContent = word
						wordWrapper.appendChild(innerSpan)
						
						parentElement.appendChild(wordWrapper)
					} else {
						// Add whitespace
						parentElement.appendChild(document.createTextNode(word))
					}
				})
			}
			
			// Function to recursively process HTML nodes
			const processHTML = (htmlString, parentElement) => {
				// Create temporary container
				const tempDiv = document.createElement('div')
				tempDiv.innerHTML = htmlString
				
				// Process each child node
				Array.from(tempDiv.childNodes).forEach(node => {
					if (node.nodeType === Node.TEXT_NODE) {
						// Handle text nodes
						processText(node.textContent, parentElement)
					} else if (node.nodeType === Node.ELEMENT_NODE) {
						// Handle HTML elements
						if (node.tagName === 'BR') {
							parentElement.appendChild(document.createElement('br'))
						} else {
							// Create new element with same tag
							const newElement = document.createElement(node.tagName.toLowerCase())
							
							// Copy attributes
							Array.from(node.attributes).forEach(attr => {
								newElement.setAttribute(attr.name, attr.value)
							})
							
							// Process the content of this element
							processHTML(node.innerHTML, newElement)
							
							// Add to parent
							parentElement.appendChild(newElement)
						}
					}
				})
			}
			
			// Process the original HTML
			processHTML(originalHTML, element)
			
			// Get the newly created spans
			innerSpans = element.querySelectorAll('.extra-split-word span')
		}
		
		// Set initial state
		gsap.set(innerSpans, {
			opacity: 0,
			y: '100%'
		})

		// Show the element now that spans are hidden
		gsap.set(element, {
			opacity: 1
		})
		
		// Check if element is in top 100vh of screen
		const elementRect = element.getBoundingClientRect()
		const isInTop100vh = elementRect.top < window.innerHeight && elementRect.bottom > 0

		console.log('🔍 isInTop100vh:', isInTop100vh)
		
		// Create scroll trigger animation
		gsap.to(innerSpans, {
			opacity: 1,
			y: 0,
			duration: 1,
			delay: 0.1,
			stagger: 0.02,
			ease: 'power2.out',
			scrollTrigger: {
				trigger: element,
				start: isInTop100vh ? 'top bottom' : 'top 80%',
				end: 'bottom 20%',
				once: true
			}
		})
	})
}

// FAQ accordion animations
function initFaqAccordions() {
	const groups = document.querySelectorAll('[data-faq-group]')
	if (!groups.length) return

	groups.forEach(group => {
		const buttons = group.querySelectorAll('.accordion-heading')
		const contents = group.querySelectorAll('.accordion-content')

		// Ensure panels start hidden with 0 height
		contents.forEach(panel => {
			panel.style.maxHeight = '0px'
			panel.style.overflow = 'hidden'
			panel.style.transition = 'max-height 300ms ease'
			panel.style.display = 'none'
		})

		buttons.forEach(btn => {
			btn.addEventListener('click', () => {
				const expanded = btn.getAttribute('aria-expanded') === 'true'
				const panelId = btn.getAttribute('aria-controls')
				const panel = document.getElementById(panelId)
				if (!panel) return

				// If opening this one, close others
				if (!expanded) {
					buttons.forEach(other => {
						if (other === btn) return
						other.setAttribute('aria-expanded', 'false')
						other.classList.remove('active')
					})
					contents.forEach(otherPanel => {
						if (otherPanel === panel) return
						otherPanel.style.maxHeight = '0px'
					})
				}

				// Toggle current
				btn.setAttribute('aria-expanded', (!expanded).toString())
				btn.classList.toggle('active', !expanded)

				if (!expanded) {
					// Opening: set to scrollHeight for smooth expand
					panel.style.display = 'block'
					// Measure after forcing reflow
					panel.style.maxHeight = '0px'
					panel.getBoundingClientRect() // force reflow
					const targetHeight = panel.scrollHeight
					panel.style.maxHeight = `${targetHeight}px`
				} else {
					// Closing
					panel.style.maxHeight = '0px'
					// Optional: after transition, hide display to avoid tab-stop
					panel.addEventListener('transitionend', function handleTransitionEnd(e) {
						if (e.propertyName === 'max-height') {
							panel.style.display = 'none'
							panel.removeEventListener('transitionend', handleTransitionEnd)
						}
					})
				}
			})
		})
	})
}


// Mobile menu functionality
function initMobileMenu() {
	const mobileMenuButton = document.getElementById('mobile-menu-button')
	const mobileMenu = document.getElementById('mobile-menu')
	const hamburgerSvg = mobileMenuButton ? mobileMenuButton.querySelector('.ham') : null
	
	// Always reset all submenus to hidden on init
	const submenusAtInit = document.querySelectorAll('.mobile-submenu')
	submenusAtInit.forEach(submenu => {
		submenu.style.maxHeight = '0'
	})
	
	if (mobileMenuButton && mobileMenu && hamburgerSvg) {
		// Remove existing event listeners to prevent duplicates
		const newButton = mobileMenuButton.cloneNode(true)
		mobileMenuButton.parentNode.replaceChild(newButton, mobileMenuButton)
		
		// Get fresh references after cloning
		const freshButton = document.getElementById('mobile-menu-button')
		const freshHamburgerSvg = freshButton ? freshButton.querySelector('.ham') : null
		
		if (freshButton && freshHamburgerSvg) {
			freshButton.addEventListener('click', () => {
				// Toggle menu visibility
				if (mobileMenu.classList.contains('opacity-0')) {
					// Reset all submenus before showing the menu
					const allSubmenusBeforeOpen = document.querySelectorAll('.mobile-submenu')
					allSubmenusBeforeOpen.forEach(submenu => {
						submenu.style.maxHeight = '0'
					})
					// Remove active classes
					document.querySelectorAll('.mobile-menu-item').forEach(item => {
						item.classList.remove('active')
					})
					// Reset arrow rotations
					document.querySelectorAll('.mobile-menu-arrow').forEach(arrow => {
						arrow.style.transform = 'rotate(0deg)'
					})
					
					// Show menu
					mobileMenu.classList.remove('opacity-0', 'invisible')
					mobileMenu.classList.add('opacity-100', 'visible')
					
					// Add class to html when menu is open
					document.documentElement.classList.add('mobile-menu-open')
					
					// Activate hamburger animation
					freshHamburgerSvg.classList.add('active')
					
					// Force hamburger to white when menu is open
					freshButton.classList.remove('text-trust-blue')
					freshButton.classList.add('text-white')
				} else {
					// Hide menu
					mobileMenu.classList.remove('opacity-100', 'visible')
					mobileMenu.classList.add('opacity-0', 'invisible')
					
					// Remove class from html when menu is closed
					document.documentElement.classList.remove('mobile-menu-open')
					
					// Deactivate hamburger animation
					freshHamburgerSvg.classList.remove('active')
					
					// Close all submenus
					const allSubmenus = document.querySelectorAll('.mobile-submenu')
					allSubmenus.forEach(submenu => {
						submenu.style.maxHeight = '0'
					})
					// Remove active classes
					document.querySelectorAll('.mobile-menu-item').forEach(item => {
						item.classList.remove('active')
					})
					// Reset arrow rotations
					document.querySelectorAll('.mobile-menu-arrow').forEach(arrow => {
						arrow.style.transform = 'rotate(0deg)'
					})
					
					// Re-check header theme to restore proper colors
					setTimeout(() => {
						if (typeof initHeaderTheme === 'function') {
							// Trigger header theme check
							const event = new Event('scroll')
							window.dispatchEvent(event)
						}
					}, 100)
				}
			})
		}
	}
	
	// Handle accordion-style submenu toggles
	const mobileToggleButtons = document.querySelectorAll('[data-mobile-toggle]')

	// Store accordion toggle handler function globally to prevent duplicates
	if (!window.mobileToggleHandler) {
		window.mobileToggleHandler = function(event) {
			event.preventDefault()
			event.stopPropagation()
			
			const button = event.currentTarget || event.target
			const toggleId = button.getAttribute('data-mobile-toggle')
			const menuItem = button.closest('.mobile-menu-item')
			const submenu = menuItem ? menuItem.querySelector(`[data-mobile-submenu="${toggleId}"]`) : null
			const arrow = button.querySelector('.mobile-menu-arrow')
			
			if (!submenu) return
			
			// Check if submenu is currently open
			const isOpen = menuItem.classList.contains('active')
			
			if (isOpen) {
				// Close submenu
				submenu.style.maxHeight = '0'
				menuItem.classList.remove('active')
				if (arrow) {
					arrow.style.transform = 'rotate(0deg)'
				}
			} else {
				// Close other open submenus first (optional - remove if you want multiple open)
				document.querySelectorAll('.mobile-menu-item.active').forEach(activeItem => {
					if (activeItem !== menuItem) {
						const activeSubmenu = activeItem.querySelector('.mobile-submenu')
						const activeArrow = activeItem.querySelector('.mobile-menu-arrow')
						if (activeSubmenu) {
							activeSubmenu.style.maxHeight = '0'
						}
						if (activeArrow) {
							activeArrow.style.transform = 'rotate(0deg)'
						}
						activeItem.classList.remove('active')
					}
				})
				
				// Temporarily set max-height to auto to get the actual height
				submenu.style.maxHeight = 'none'
				const height = submenu.scrollHeight
				submenu.style.maxHeight = '0'
				
				// Use requestAnimationFrame to ensure the transition works
				requestAnimationFrame(() => {
					submenu.style.maxHeight = height + 'px'
					menuItem.classList.add('active')
					if (arrow) {
						arrow.style.transform = 'rotate(180deg)'
					}
				})
			}
		}
	}
	
	// Attach toggle handlers to all toggle buttons
	mobileToggleButtons.forEach(button => {
		// Remove existing event listener to prevent duplicates
		button.removeEventListener('click', window.mobileToggleHandler)
		// Add the event listener
		button.addEventListener('click', window.mobileToggleHandler)
	})
}

// Header theme detection - only for section elements
function initHeaderTheme() {
	const header = document.querySelector('.site-header')
	const mobileMenuButton = document.getElementById('mobile-menu-button')
	const mobileMenu = document.getElementById('mobile-menu')

	// Scroll direction tracking for hide/show functionality
	let lastScrollY = window.scrollY
	let scrollThreshold = 100 // Minimum scroll distance before hiding/showing
	const logoShrinkThreshold = 50 // Scroll distance after which logo shrinks (desktop only)

	// Logo shrink on scroll (≥1024px only)
	function updateHeaderScrolledState() {
		if (window.innerWidth < 1024) {
			header.classList.remove('header-scrolled')
			return
		}
		if (window.scrollY > logoShrinkThreshold) {
			header.classList.add('header-scrolled')
		} else {
			header.classList.remove('header-scrolled')
		}
	}

	// Function to handle scroll direction
	function handleScrollDirection() {
		const currentScrollY = window.scrollY
		const scrollDifference = Math.abs(currentScrollY - lastScrollY)

		// Logo shrink state (runs every scroll when ≥1024px)
		updateHeaderScrolledState()
		
		// Only update if scroll difference is significant (prevents jitter)
		if (scrollDifference < 5) return
		
		// At the top of the page, always show header
		if (currentScrollY < scrollThreshold) {
			header.classList.remove('scrolling', 'up')
			lastScrollY = currentScrollY
			return
		}
		
		// Determine scroll direction
		if (currentScrollY > lastScrollY) {
			// Scrolling down - hide header
			header.classList.remove('up')
			header.classList.add('scrolling')
		} else if (currentScrollY < lastScrollY) {
			// Scrolling up - show header
			header.classList.remove('scrolling')
			header.classList.add('up')
		}
		
		lastScrollY = currentScrollY
	}
	
	// Throttled scroll handler for better performance
	let scrollDirectionTicking = false
	function onScroll() {
		if (!scrollDirectionTicking) {
			requestAnimationFrame(() => {
				handleScrollDirection()
				scrollDirectionTicking = false
			})
			scrollDirectionTicking = true
		}
	}
	
	// Add scroll listener for hide/show functionality (works on all screen sizes)
	window.addEventListener('scroll', onScroll, { passive: true })
	window.addEventListener('resize', updateHeaderScrolledState)

	// Initialize scroll state
	handleScrollDirection()
	updateHeaderScrolledState()
	
	// Function to check what section is at 100px from top
	function checkElementAt100px() {
		
		const sections = document.querySelectorAll('section, footer')
		
		let targetSection = null
		let candidates = []
		
		// Find the section that contains the 100px point
		for (let section of sections) {
			const rect = section.getBoundingClientRect()
			const isAt100px = rect.top <= 100 && rect.bottom > 100
			
			// Log all sections for debugging
			candidates.push({
				element: section,
				top: rect.top,
				bottom: rect.bottom,
				isAt100px: isAt100px,
				classes: Array.from(section.classList),
				visible: rect.top < window.innerHeight && rect.bottom > 0
			})
			
			// Check if 100px from top falls within this section
			if (isAt100px) {
				targetSection = section;
				break
			}
		}
		
		// Check if mobile menu is open
		const isMobileMenuOpen = mobileMenu && !mobileMenu.classList.contains('opacity-0')
		
		// Check if target section has the required classes
		if (targetSection) {
			const hasBgImage = targetSection.classList.contains('has-bg-image')
			const hasDarkBg = targetSection.classList.contains('has-dark-bg')
			const allClasses = Array.from(targetSection.classList)
			
			if (hasBgImage || hasDarkBg) {
				header.classList.remove('header-dark')
				header.classList.add('header-light')
				
				// Update mobile button color - white when on dark sections
				if (mobileMenuButton) {
					mobileMenuButton.classList.remove('text-trust-blue')
					mobileMenuButton.classList.add('text-white')
				}
			} else {
				header.classList.remove('header-light')
				header.classList.add('header-dark')
				
				// Update mobile button color - trust-blue when on light sections
				if (mobileMenuButton) {
					mobileMenuButton.classList.remove('text-white')
					mobileMenuButton.classList.add('text-trust-blue')
				}
			}
		} else {
			// Default to dark if no section found
			header.classList.remove('header-light')
			header.classList.add('header-dark')
			
			// Update mobile button color - trust-blue by default (light sections)
			if (mobileMenuButton) {
				mobileMenuButton.classList.remove('text-white')
				mobileMenuButton.classList.add('text-trust-blue')
			}
		}
		
		// Override colors when mobile menu is open
		if (isMobileMenuOpen) {
			// When mobile menu is open, both logo and hamburger should be white
			if (mobileMenuButton) {
				mobileMenuButton.classList.remove('text-trust-blue')
				mobileMenuButton.classList.add('text-white')
			}
		}
	}
	
	// Use Intersection Observer for better performance
	const observer = new IntersectionObserver((entries) => {
		checkElementAt100px()
	}, {
		root: null,
		rootMargin: '0px',
		threshold: 0
	})
	
	// Observe only sections
	const sectionsToObserve = document.querySelectorAll('section, footer')
	sectionsToObserve.forEach((section, index) => {
		observer.observe(section)
	})
	
	// Also check on scroll for more responsive updates
	let ticking = false
	function updateHeaderTheme() {
		if (!ticking) {
			requestAnimationFrame(() => {
				checkElementAt100px()
				ticking = false
			})
			ticking = true
		}
	}
	
	window.addEventListener('scroll', updateHeaderTheme)
	checkElementAt100px()
}

// Remove display: none from mega menus after page load to prevent FOUC
function initMegaMenuDisplay() {
	setTimeout(() => {
		const megaMenus = document.querySelectorAll('.group\\/nav-group > div');
		megaMenus.forEach(menu => {
			menu.style.display = '';
		});
	}, 500);
}

// Comparison Block Animation
function initComparisonBlockAnimation() {
	// Wait for SVG to be fully rendered
	requestAnimationFrame(() => {
		setTimeout(() => {
			const svg = document.querySelector('.comparisonBlock #infographics-revised1_1_1_');
			console.log('🔍 SVG:', svg);
			if (!svg) return;

			const referenceGroup = svg.querySelector('#Group_410');
			if (!referenceGroup) return;

			// Get all direct child groups (use :scope or direct children)
			const allGroups = Array.from(svg.children).filter(child => child.tagName === 'g');
			if (allGroups.length === 0) return;
			
			// Define the specific order we want
			const groupOrder = ['Group_404', 'Group_403', 'Group_402', 'Group_401', 'Group_400', 'Group_399'];
			
			// Filter and order groups by the specified IDs
			const groups = groupOrder
				.map(id => allGroups.find(g => g.id === id))
				.filter(g => g !== undefined);
			
			if (groups.length === 0) return;

			// Get reference width from Group_410
			const refRect = referenceGroup.getBoundingClientRect();
			const refWidth = refRect.width;
			const svgRect = svg.getBoundingClientRect();
			const svgCenterX = svgRect.width / 2;
			const svgCenterY = svgRect.height / 2;

			// Define scale multipliers for main groups in order
			// Order: Group_404, Group_403, Group_402, Group_401, Group_400, Group_399
			const mainScaleMultipliers = [0.6, 0.4, 0.6, 0.6, 0.6, 1]; // Scale multipliers for main groups
			
			// Define nested group IDs for each main group in order
			// Order: Group_404, Group_403, Group_402, Group_401, Group_400, Group_399
			const nestedGroupIds = ['Group_405', 'Group_406', 'Group_407', 'Group_408', 'Group_409', 'Group_410'];
			
			// Define scale multipliers for nested groups in order
			// Order: Group_404, Group_403, Group_402, Group_401, Group_400, Group_399
			const nestedScaleMultipliers = [15, 5, 2.5, 1.5, 1, 1]; // Scale multipliers for nested groups
			
			// Define additional transform modifications for each group (x, y offsets)
			// Order: Group_404, Group_403, Group_402, Group_401, Group_400, Group_399
			const transformModifications = [
				{ x: 0, y: 0 }, // Group_404
				{ x: 0, y: 50 }, // Group_403
				{ x: 0, y: 0 }, // Group_402
				{ x: 0, y: 0 }, // Group_401
				{ x: 0, y: 0 }, // Group_400
				{ x: 0, y: 0 }  // Group_399
			];
			
			// Store original transforms and positions
			const groupData = groups.map((group, index) => {
				let bbox;
				try {
					bbox = group.getBBox();
				} catch (e) {
					// If getBBox fails, use getBoundingClientRect and convert to SVG coordinates
					const rect = group.getBoundingClientRect();
					const svgRect = svg.getBoundingClientRect();
					const svgPoint = svg.createSVGPoint();
					svgPoint.x = rect.left - svgRect.left;
					svgPoint.y = rect.top - svgRect.top;
					const svgMatrix = svg.getScreenCTM().inverse();
					const transformedPoint = svgPoint.matrixTransform(svgMatrix);
					bbox = {
						x: transformedPoint.x,
						y: transformedPoint.y,
						width: (rect.width / svgRect.width) * svg.viewBox.baseVal.width,
						height: (rect.height / svgRect.height) * svg.viewBox.baseVal.height
					};
				}
				
				const rect = group.getBoundingClientRect();
				const groupWidth = rect.width || bbox.width;
				const groupHeight = rect.height || bbox.height;
				
				// Calculate scale for main group - use multiplier for each group
				const baseScale = refWidth / groupWidth;
				const mainMultiplier = mainScaleMultipliers[index] || 0.6;
				const scale = baseScale * mainMultiplier;
				
				// Get original transform
				const transform = group.getAttribute('transform') || '';
				
				// Calculate center of group in SVG coordinates
				const groupCenterX = bbox.x + bbox.width / 2;
				const groupCenterY = bbox.y + bbox.height / 2;
				
				// Find the nested group by ID (Group_405, Group_406, etc.)
				const nestedGroupId = nestedGroupIds[index];
				const nestedGroup = nestedGroupId ? svg.querySelector(`#${nestedGroupId}`) : null;
				let nestedTransform = '';
				let nestedScale = 1;
				
				if (nestedGroup) {
					nestedTransform = nestedGroup.getAttribute('transform') || '';
					// Apply the scale multiplier to the nested group based on its position
					nestedScale = nestedScaleMultipliers[index] || 1;
				}
			
			// Find all lines in this group for dash animation
			const lines = Array.from(group.querySelectorAll('line, path[stroke]'));
			const lineLengths = lines.map(line => {
				try {
					const length = line.getTotalLength();
					if (length > 0) {
						line.style.strokeDasharray = length;
						line.style.strokeDashoffset = length;
					}
					return length;
				} catch (e) {
					return 0;
				}
			}).filter(length => length > 0);
			
			// Find all text/path elements that represent text (paths with specific fills)
			const textPaths = Array.from(group.querySelectorAll('path')).filter(path => {
				const fill = path.getAttribute('fill');
				return fill && (fill.includes('#71f0ba') || fill.includes('#93b0c4') || fill.includes('#efedea') || fill.includes('#d8d2ca') || fill.includes('#fff'));
			});
			const textElements = Array.from(group.querySelectorAll('text'));
			const texts = [...textElements, ...textPaths];
			
				// Get transform modification for this group
				const transformMod = transformModifications[index] || { x: 0, y: 0 };
				
				return {
					group,
					scale,
					originalTransform: transform,
					originalCenterX: groupCenterX,
					originalCenterY: groupCenterY,
					originalWidth: groupWidth,
					originalHeight: groupHeight,
					lines,
					lineLengths,
					texts,
					bbox,
					nestedGroup,
					nestedTransform,
					nestedScale,
					transformModX: transformMod.x,
					transformModY: transformMod.y
				};
			});

		// Get SVG viewBox center for centering calculation
		const svgViewBox = svg.viewBox.baseVal;
		const svgViewBoxCenterX = svgViewBox.width / 2;
		const svgViewBoxCenterY = svgViewBox.height / 2;
		
		// Set initial state: scale all groups to reference size and center them
		groupData.forEach(({ group, scale, originalCenterX, originalCenterY, lines, texts, originalTransform, nestedGroup, nestedScale, transformModX, transformModY }, index) => {
			// Calculate translation to center (using group's center, not bbox center)
			// Add custom transform modifications for each group
			const translateX = svgViewBoxCenterX - originalCenterX + transformModX;
			const translateY = svgViewBoxCenterY - originalCenterY + transformModY;
			
			// Set z-index for main group (1, 2, 3, 4, 5, 6 * 10)
			const groupZIndex = (index + 1) * 10;
			group.style.zIndex = groupZIndex;
			group.style.position = 'relative'; // Needed for z-index to work
			
			// Set z-index for children within each group (first: 2, second: 3, third: 1)
			const children = Array.from(group.children);
			children.forEach((child, childIndex) => {
				if (child.tagName === 'g' || child.tagName === 'path' || child.tagName === 'circle' || child.tagName === 'text' || child.tagName === 'line') {
					let childZIndex;
					if (childIndex === 0) childZIndex = 2;
					else if (childIndex === 1) childZIndex = 3;
					else if (childIndex === 2) childZIndex = 1;
					else childZIndex = childIndex + 1; // For items beyond 3rd
					
					child.style.zIndex = childZIndex;
					child.style.position = 'relative';
				}
			});
			
			// Clear any existing transform first and store original
			// Remove the original transform attribute temporarily so GSAP can control it
			if (originalTransform) {
				group.removeAttribute('transform');
			}
			
			// Set initial scaled and centered position using GSAP properties
			gsap.set(group, {
				x: translateX,
				y: translateY,
				scale: scale,
				transformOrigin: 'center center',
				opacity: 0 // Hide after positioning
			});
			
			// Also scale the nested group (2nd level group) from center
			if (nestedGroup) {
				if (nestedGroup.getAttribute('transform')) {
					nestedGroup.removeAttribute('transform');
				}
				gsap.set(nestedGroup, {
					scale: nestedScale,
					transformOrigin: 'center center'
				});
			}
			
			// Hide all text initially
			texts.forEach(text => {
				gsap.set(text, { opacity: 0 });
			});
		});

		// Refresh ScrollTrigger to account for any layout changes
		ScrollTrigger.refresh();
		
		// Create master timeline
		const masterTL = gsap.timeline({
			scrollTrigger: {
				trigger: '.comparisonBlock',
				start: 'top 80%',
				toggleActions: 'play none none none',
				once: true,
				markers: false, // Set to true for debugging
				onEnter: () => {
					console.log('Comparison block animation triggered');
				}
			}
		});

		// Animate each group sequentially (one completes before next starts)
		groupData.forEach((data, index) => {
			const { group, originalTransform, originalCenterX, originalCenterY, lines, lineLengths, texts, nestedGroup, nestedTransform, nestedScale, transformModX, transformModY } = data;
			
			// Parse original transform matrix to get the translation values
			// Matrix format: matrix(a, b, c, d, e, f) where e = translateX, f = translateY
			let originalMatrixX = 0;
			let originalMatrixY = 0;
			if (originalTransform) {
				const matrixMatch = originalTransform.match(/matrix\(([^)]+)\)/);
				if (matrixMatch) {
					const values = matrixMatch[1].split(',').map(v => parseFloat(v.trim()));
					if (values.length >= 6) {
						originalMatrixX = values[4]; // e = translateX
						originalMatrixY = values[5]; // f = translateY
					}
				}
			}
			
			// Group timeline - each group completes fully before next starts
			const groupTL = gsap.timeline();
			
			// 1. Fade in the group (already centered and scaled from initial setup)
			groupTL.to(group, {
				opacity: 1,
				duration: 0.5, // Slower than before but still quicker than original
				ease: 'power2.out'
			});
			
			// 2. Draw in lines (if any)
			if (lines.length > 0 && lineLengths.length > 0) {
				lines.forEach((line, lineIndex) => {
					if (lineLengths[lineIndex] > 0) {
						groupTL.to(line, {
							strokeDashoffset: 0,
							duration: 0.65, // Slower than before but still quicker than original
							ease: 'power1.inOut'
						}, lineIndex * 0.08); // Slight stagger for multiple lines
					}
				});
			}
			
			// 3. Fade in text
			if (texts.length > 0) {
				groupTL.to(texts, {
					opacity: 1,
					duration: 0.36, // 0.6 * 0.6 (40% quicker)
					ease: 'power2.out',
					stagger: 0.03 // 0.05 * 0.6 (40% quicker stagger)
				}, '-=0.18'); // 0.3 * 0.6 (40% quicker offset)
			}
			
			// 4. Move back to original position and scale
			// Animate to the original matrix translation values
			groupTL.to(group, {
				x: originalMatrixX,
				y: originalMatrixY,
				scale: 1,
				duration: 0.6, // 1 * 0.6 (40% quicker)
				ease: 'power2.inOut',
				onComplete: () => {
					// Clear GSAP transform properties and restore original transform
					gsap.set(group, { clearProps: 'all' });
					if (originalTransform) {
						group.setAttribute('transform', originalTransform);
					}
				}
			}, '-=0.12'); // 0.2 * 0.6 (40% quicker offset)
			
			// 5. Also animate nested group back to original scale
			if (nestedGroup) {
				groupTL.to(nestedGroup, {
					scale: 1,
					duration: 0.6, // 1 * 0.6 (40% quicker)
					ease: 'power2.inOut',
					onComplete: () => {
						// Clear GSAP transform and restore original nested transform
						gsap.set(nestedGroup, { clearProps: 'all' });
						if (nestedTransform) {
							nestedGroup.setAttribute('transform', nestedTransform);
						}
					}
				}, '-=0.6'); // 1 * 0.6 (40% quicker offset)
			}
			
			// Add to master timeline sequentially (no delay - each completes before next starts)
			masterTL.add(groupTL);
		});
		}, 100);
	});
}

// Technology Diagram Animation
function initTechnologyDiagramAnimation() {
	const illustration = document.querySelector('#illistration-animation');
	if (!illustration) return;

	// Fade in each direct child <g> using stagger when SVG enters viewport
	gsap.fromTo(
		"#illistration-animation > g",
		{
			opacity: 0,
		},
		{
			opacity: 1,
			duration: 1,
			ease: "power2.inOut",
			stagger: 0.05,
			scrollTrigger: {
				trigger: "#illistration-animation",
				start: "center 90%",
				toggleActions: "play none none reverse",
				markers: false,
			},
		}
	);

	// Animate arrow-top when SVG enters viewport
	gsap.fromTo(
		"#arrow-top",
		{
			opacity: 0,
			y: -50,
		},
		{
			opacity: 1,
			y: 0,
			duration: 1,
			ease: "power2.inOut",
			delay: 0.2,
			scrollTrigger: {
				trigger: "#illistration-animation",
				start: "center 90%",
				toggleActions: "play none none reverse",
				markers: false,
			},
		}
	);

	// Animate arrow-bottom when SVG enters viewport
	gsap.fromTo(
		"#arrow-bottom",
		{
			opacity: 0,
			y: 50,
		},
		{
			opacity: 1,
			y: 0,
			duration: 1,
			delay: 0.2,
			ease: "power2.inOut",
			scrollTrigger: {
				trigger: "#illistration-animation",
				start: "center 90%",
				toggleActions: "play none none reverse",
				markers: false,
			},
		}
	);

	// Continuous infinite rotation for fan
	gsap.to("#fan", {
		rotation: 360,
		duration: 10,
		ease: "none",
		repeat: -1,
		transformOrigin: "center center",
	});

	// Bubbles rising upward like real bubbles floating to surface
	const bubblePaths = gsap.utils.toArray("#bubbles path");

	bubblePaths.forEach((bubble) => {
		// Random values for each bubble
		const riseDistance = gsap.utils.random(10, -30);
		const horizontalDrift = gsap.utils.random(-10, 10);
		const randomDuration = gsap.utils.random(2, 4);
		const randomDelay = gsap.utils.random(0, 3);
		const randomScale = gsap.utils.random(0.8, 1.2);

		// Set initial state - start at bottom
		gsap.set(bubble, {
			opacity: 0.8,
			y: 0,
			x: 0,
			transformOrigin: "center center",
		});

		// Create rising bubble animation
		const bubbleTimeline = gsap.timeline({
			repeat: -1,
			delay: randomDelay,
		});

		bubbleTimeline
			.to(bubble, {
				opacity: 1,
				scale: 0,
				duration: randomDuration,
				ease: "power1.out",
			})
			.to(bubble, {
				y: 0,
				x: 0,
				opacity: 1,
				scale: 1,
				duration: 0,
				ease: "none",
			});
	});
}

// Latest Vacancies block: tab switching + Swiper (1.1 slides mobile, 3 desktop, 20px gap, no nav/pagination)
function initLatestVacanciesTabs() {
	const blocks = document.querySelectorAll('.latest-vacancies');
	blocks.forEach((block) => {
		const tabs = block.querySelectorAll('[role="tab"]');
		const panels = block.querySelectorAll('[role="tabpanel"]');
		tabs.forEach((tab) => {
			tab.addEventListener('click', () => {
				const id = tab.getAttribute('data-tab');
				tabs.forEach((t) => t.setAttribute('aria-selected', 'false'));
				tab.setAttribute('aria-selected', 'true');
				panels.forEach((p) => {
					if (p.getAttribute('data-panel') === id) {
						p.classList.remove('hidden');
					} else {
						p.classList.add('hidden');
					}
				});
			});
		});
	});
}

function initLatestVacanciesSwiper() {
	const grids = document.querySelectorAll('.latest-vacancies__grid.swiper');
	grids.forEach((el) => {
		if (el.querySelectorAll('.swiper-slide').length === 0) return;
		new Swiper(el, {
			slidesPerView: 1.1,
			spaceBetween: 16,
			breakpoints: {
				640: { slidesPerView: 1.5 },
				830: { slidesPerView: 2.2 },
				1200: { slidesPerView: 3 },
			},
		});
	});
}

// Initialize all functionality when DOM is ready
function init() {
	// Initialize smooth scroll first (needed for ScrollTrigger)
	initSmoothScroll();
	
	// Remove display: none from mega menus after load
	initMegaMenuDisplay();
	
	// Initialize all other functions
	initFadeUpAnimations();
	initCounterAnimations();
	initTextAnimations();
	initFaqAccordions();
	initMobileMenu();
	initHeaderTheme();
	initLatestVacanciesTabs();
	initLatestVacanciesSwiper();
	initListingAjax();
	initTechnologyDiagramAnimation();
	initComparisonBlockAnimation();
}

// Run initialization when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	// DOM is already ready
	init();
}